import { ReactComponent as ShieldIcon } from '@Assets/icons/shield.svg';
import {
  FontWeight,
  LiliLinkButton,
  LiliLinkType,
  LiliOtp,
  Spinner,
  SpinnerColor,
} from '@lili/lili-frontend-common';
import { Text, Heading } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { useVerifyPhoneLogic } from './hooks/useVerifyPhoneLogic';
import { useBoundStore } from '@Store/useBoundStore';
import { OnboardingFormFields } from '@Types/index';
import { convertToTimeFormat, getUserFirstName, maskPhoneNumber } from '@Services/utils/utils';
import { ControllerWrapper } from '@Hooks/index';
import { FormProvider } from 'react-hook-form';
import { useBlocker } from '@tanstack/react-router';
import { RoutesMap } from '@Router/router.types';
import { routesModel } from '@Router/router.consts';

export const VerifyPhone = () => {
  const { t } = useTranslation(['inviteTeamMember']);
  const {
    customerData: { firstName, phoneNumber },
  } = useBoundStore((state) => state.customer);
  const { authorizedUserFullName } = useBoundStore((state) => state.authOnboardingSlice);
  const userName = firstName || getUserFirstName(authorizedUserFullName);
  const { handleVerifyCode, handleResendCode, isLoading, formMethods, isSendingMfaCode, timeLeft } =
    useVerifyPhoneLogic();

  useBlocker({
    shouldBlockFn: ({ next }) => {
      if (next.routeId === routesModel[RoutesMap.SET_PASSWORD].path) {
        return false;
      }
      return true;
    },
  });

  const maskedPhoneNumber = phoneNumber ? maskPhoneNumber(phoneNumber) : '';

  return (
    <FormProvider {...formMethods}>
      <div className='flex flex-col items-center justify-between mt-[24px] md:mt-[127px] w-full max-w-[450px] gap-[24px] mx-[24px] md:mx-[0px]'>
        <div className='flex flex-col items-center gap-[24px]'>
          <ShieldIcon className='w-[90px] h-[90px] text-primary' />
          <div>
            <Heading.H5 fontWeight={FontWeight.SEMIBOLD} className='text-center mb-[4px]'>
              {t('verifyPhone.title', { firstName: userName })}
            </Heading.H5>

            <Text.H9 className='text-text-main-secondary text-center'>
              {t('verifyPhone.subtitle', { phoneNumber: maskedPhoneNumber })}
            </Text.H9>
          </div>
          <ControllerWrapper name={OnboardingFormFields.PHONE_OTP}>
            {({ error }) => (
              <>
                <LiliOtp
                  id='verify-phone-otp'
                  maxLength={6}
                  onCompleted={handleVerifyCode}
                  disabled={isLoading || isSendingMfaCode}
                  errorMessage={error?.message}
                  autoFocus
                />
                {isLoading && (
                  <div className='self-center -mt-[32px]'>
                    <Spinner height='24px' width='24px' spinnerColor={SpinnerColor.Blue} />
                  </div>
                )}
              </>
            )}
          </ControllerWrapper>
        </div>

        <div className='flex text-center justify-center flex-col gap-[24px] mb-[40px]'>
          <div className='flex text-center justify-center flex-col gap-[8px]'>
            <Heading.H9 fontWeight={FontWeight.SEMIBOLD}>
              {t('verifyPhone.noCodeReceived')}
            </Heading.H9>
            <Heading.H9>{t('verifyPhone.checkSpam')}</Heading.H9>

            {!timeLeft && !isSendingMfaCode && (
              <LiliLinkButton
                type={LiliLinkType.QUIET}
                title={t('verifyPhone.resendCode')}
                id='verify-phone-resend-code'
                onClick={handleResendCode}
                className='self-center'
              />
            )}
            {isSendingMfaCode && (
              <Spinner height='24px' width='24px' spinnerColor={SpinnerColor.Blue} />
            )}
            {timeLeft && !isSendingMfaCode && (
              <div className='flex items-center gap-[4px] self-center'>
                <Heading.H9>
                  {t('verifyPhone.resendCodeTimer', { timeLeft: convertToTimeFormat(timeLeft) })}
                </Heading.H9>
              </div>
            )}
          </div>
          <Text.H10 className='text-text-main-tertiary md:px-[10px] px-[4px] md:whitespace-pre-line'>
            {t('verifyPhone.disclaimer')}
          </Text.H10>
        </div>
      </div>
    </FormProvider>
  );
};
